import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Icon from "@/components/common/Icon";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useUserContext, useActiveFarmContext } from "@/contexts";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";
import { useDrawerPreference } from "@/hooks/useDrawerPreference";
import eitFoodEuLogo from "@/assets/eit-food-eu-logo.png";

const drawerWidth = 240;

const USER_LINKS = [
  {
    labelKey: "nav.onboardingGuide",
    icon: <Icon name="book-open" />,
    href: "/onboarding-guide",
  },
  { labelKey: "nav.farms", icon: <Icon name="tractor" />, href: "/farms" },
  {
    labelKey: "nav.fieldBoundaries",
    icon: <Icon name="map" />,
    href: "/farms/manage",
  },
  {
    labelKey: "nav.fieldRecords",
    icon: <Icon name="clipboard-list" />,
    href: "/farms/fields",
  },
  {
    labelKey: "nav.coverTargets",
    icon: <Icon name="sprout" />,
    href: "/farms/analysis",
  },
  {
    labelKey: "nav.farmCover",
    icon: <Icon name="bar-chart-3" />,
    href: "/farms/cover",
  },
  {
    labelKey: "nav.daysSince",
    icon: <Icon name="clock" />,
    href: "/farms/days-since",
  },
  {
    labelKey: "nav.myInvitations",
    icon: <Icon name="mail" />,
    href: "/invitations",
  },
];

const FORECAST_DASHBOARD_LINK = {
  labelKey: "nav.growthForecast",
  icon: <Icon name="trending-up" />,
  href: "/farms/dashboard-forecast",
};

// Not an admin link: allowlisted non-admins get this entry too.
const FORECAST_ANALYSIS_LINK = {
  labelKey: "nav.forecastAnalysis",
  icon: <Icon name="bar-chart-3" />,
  href: "/forecast-analysis",
};

const SATELLITE_DETECTION_ANALYSIS_LINK = {
  labelKey: "nav.satelliteDetectionAnalysis",
  icon: <Icon name="satellite" />,
  href: "/satellite-detection-analysis",
};

const ADMIN_LINKS = [
  {
    labelKey: "nav.regionalForecast",
    icon: <Icon name="wind" />,
    href: "/circulations",
  },
  {
    labelKey: "nav.scan",
    icon: <Icon name="satellite" />,
    href: "/scan",
  },
  {
    labelKey: "nav.weatherForecast",
    icon: <Icon name="cloud-sun" />,
    href: "/farms/weather",
  },
  {
    labelKey: "nav.growthForecastPoc",
    icon: <Icon name="layout-dashboard" />,
    href: "/farms/dashboard",
  },
  {
    labelKey: "nav.forecastTable",
    icon: <Icon name="table" />,
    href: "/farms/outlook",
  },
  {
    labelKey: "nav.forecastMap",
    icon: <Icon name="map-pinned" />,
    href: "/farms/field-outlook",
  },
  {
    labelKey: "nav.admin",
    icon: <Icon name="shield-check" />,
    href: "/admin",
  },
];

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: 64,
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
  ],
}));

export default function AgribotAppBar({ children }) {
  const theme = useTheme();
  const location = useLocation();
  const { loginWithRedirect, logout } = useAuth0();
  const { t } = useTranslation();
  const {
    user,
    isAuthenticated,
    isAdmin,
    forecastDashboardEnabled,
    forecastAnalysisEnabled,
  } = useUserContext();
  const { activeFarm, farms, setActiveFarm } = useActiveFarmContext();

  const [open, setOpen] = useDrawerPreference();
  const [anchorEl, setAnchorEl] = React.useState(null);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleActiveFarmChange = (event) => {
    const selectedId = String(event.target.value);
    const farm = farms.find((f) => String(f.id) === selectedId);
    setActiveFarm(farm || null);
  };

  const returnTo =
    `${location.pathname}${location.search}${location.hash}` || "/";
  const railOpen = isAuthenticated && open;
  // Capability links sit just above My Invitations, which stays last.
  const capabilityLinks = [
    ...(forecastDashboardEnabled ? [FORECAST_DASHBOARD_LINK] : []),
    ...(forecastAnalysisEnabled
      ? [FORECAST_ANALYSIS_LINK, SATELLITE_DETECTION_ANALYSIS_LINK]
      : []),
  ];
  const userLinks = capabilityLinks.length
    ? [
        ...USER_LINKS.slice(0, -1),
        ...capabilityLinks,
        USER_LINKS[USER_LINKS.length - 1],
      ]
    : USER_LINKS;

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <CssBaseline />
      <AppBar position="fixed" open={railOpen}>
        <Toolbar>
          {isAuthenticated && (
            <IconButton
              color="inherit"
              aria-label={t("openDrawer")}
              onClick={handleDrawerOpen}
              edge="start"
              sx={[{ marginRight: 5 }, open && { display: "none" }]}
            >
              <Icon name="menu" />
            </IconButton>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
            <Typography
              variant="h6"
              noWrap
              sx={{ fontWeight: 700, display: "flex", alignItems: "center" }}
            >
              <Box component="span" sx={{ color: "text.primary" }}>
                Pasture
              </Box>

              <Box
                component="span"
                sx={{ color: "secondary.main", ml: 1, fontWeight: 700 }}
              >
                AI
              </Box>
            </Typography>
          </Box>

          {isAuthenticated && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 2,
              }}
            >
              {farms?.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    data-testid="farm-selector"
                    value={activeFarm ? String(activeFarm.id) : "no-active"}
                    onChange={handleActiveFarmChange}
                    SelectDisplayProps={{
                      "data-testid": "farm-selector-display",
                    }}
                    MenuProps={{
                      MenuListProps: {
                        "data-testid": "farm-selector-menu",
                      },
                    }}
                  >
                    {farms.map((f) => (
                      <MenuItem
                        key={f.id}
                        value={String(f.id)}
                        data-testid={`farm-option-${String(f.id)}`}
                      >
                        {f.name}
                      </MenuItem>
                    ))}
                    {!activeFarm && (
                      <MenuItem
                        key={"no-active"}
                        value={"no-active"}
                        data-testid="farm-option-no-active"
                      >
                        {t("noFarmSelected")}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              )}

              <LanguageSwitcher />

              <IconButton
                size="large"
                aria-label={t("accountMenu")}
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                color="inherit"
              >
                <Avatar alt={user?.name} src={user?.picture} />
              </IconButton>

              <Menu
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem disabled>
                  <Typography>{user?.name}</Typography>
                </MenuItem>
                <MenuItem disabled>
                  <Typography>{user?.email}</Typography>
                </MenuItem>
                <Divider />
                <MenuItem
                  component={RouterLink}
                  to="/profile"
                  onClick={handleClose}
                >
                  {t("profile")}
                </MenuItem>
                <MenuItem
                  onClick={() => logout({ returnTo: window.location.origin })}
                >
                  {t("logout")}
                </MenuItem>
              </Menu>
            </Box>
          )}

          {!isAuthenticated && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <LanguageSwitcher />
              <Button
                variant="contained"
                color="secondary"
                onClick={() =>
                  loginWithRedirect({
                    appState: { returnTo },
                  })
                }
              >
                {t("logIn")}
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>
      {isAuthenticated && (
        <Drawer
          variant="permanent"
          open={open}
          sx={{ "& .MuiDrawer-paper": { overflowY: "hidden" } }}
        >
          <DrawerHeader sx={{ flexShrink: 0 }}>
            <IconButton onClick={handleDrawerClose}>
              {theme.direction === "rtl" ? (
                <Icon name="chevron-right" />
              ) : (
                <Icon name="chevron-left" />
              )}
            </IconButton>
          </DrawerHeader>
          <Divider />
          <List sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {userLinks.map(({ labelKey, icon, href }) => (
              <ListItem key={labelKey} disablePadding sx={{ display: "block" }}>
                <ListItemButton
                  component={RouterLink}
                  to={href}
                  selected={location.pathname === href}
                  sx={[
                    { minHeight: 48 },
                    open
                      ? { justifyContent: "initial" }
                      : { justifyContent: "center" },
                  ]}
                >
                  <ListItemIcon
                    sx={[
                      { minWidth: 0, justifyContent: "center" },
                      open ? { mr: 3 } : { mr: "auto" },
                    ]}
                  >
                    {icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={t(labelKey)}
                    sx={[open ? { opacity: 1 } : { opacity: 0 }]}
                  />
                </ListItemButton>
              </ListItem>
            ))}
            <Divider />
            {isAdmin &&
              ADMIN_LINKS.map(({ labelKey, icon, href }) => (
                <ListItem
                  key={labelKey}
                  disablePadding
                  sx={{ display: "block" }}
                >
                  <ListItemButton
                    component={RouterLink}
                    to={href}
                    selected={location.pathname === href}
                    sx={[
                      { minHeight: 48 },
                      open
                        ? { justifyContent: "initial" }
                        : { justifyContent: "center" },
                    ]}
                  >
                    <ListItemIcon
                      sx={[
                        { minWidth: 0, justifyContent: "center" },
                        open ? { mr: 3 } : { mr: "auto" },
                      ]}
                    >
                      {icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={t(labelKey)}
                      sx={[open ? { opacity: 1 } : { opacity: 0 }]}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
          </List>
          {open && (
            <Box
              component="a"
              href="https://www.eitfood.eu/"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "block",
                flexShrink: 0,
                p: 1.5,
                borderTop: 1,
                borderColor: "divider",
                bgcolor: "#fff",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: -2,
                },
              }}
            >
              <Box
                component="img"
                src={eitFoodEuLogo}
                alt="EIT Food — Co-funded by the European Union"
                width={1837}
                height={310}
                sx={{
                  display: "block",
                  width: 176,
                  maxWidth: "100%",
                  height: "auto",
                  mx: "auto",
                }}
              />
            </Box>
          )}
        </Drawer>
      )}
      <Box
        className="appbar-main-box"
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: "auto",
        }}
      >
        <DrawerHeader />
        {children}
      </Box>
    </Box>
  );
}
